import {
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Chip,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Paper,
    Radio,
    RadioGroup,
    Slider,
    Stack,
} from "@mui/material";
import React, { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAppSelector } from "#store";
import { PageContainer } from "#pages/PageContainer";
import { Json } from "#common/Json";
import { H1, H2, H3, P } from "#common/headings";
import { ColorScheme, QueryLanguage } from "#store/ui/uiTypes";
import {
    setColumnFav,
    setComparisonWeight,
    setFavoriteWeight,
    setInterpretationsLimit,
    setMaxResultsPerTable,
    setQueryLanguage,
    setQueryWeight,
    setRelevanceWeight,
    setTheme,
    toggleColumnFav,
    toggleInterpretationFav,
} from "#store/ui/uiActions";
import { stringToEnumValue } from "#/util/enums";
import { ApplicationState } from "#store/types";
import { selectDatasources } from "#selectors";
import DatasourceSelector from "#components/DatasourceSelector";
import { DoiConfiguration } from "../Model/Doi/DoiConfiguration";
import columFavPresets from "./Settings/columnFavoritePresets";
import { SessionsComponent } from "./Settings/SessionsComponent";

function interpretationsValue(value: number) {
    return `${value} interpretations`;
}

export const SettingsPage = () => {
    // Redux: Map variables from redux store state
    const currentTheme = useSelector(
        (state: ApplicationState) => state.uiState.theme
    );

    const currentInterpretationsLimit = useAppSelector(
        state => state.uiState.maxInterpretationsLimit
    );

    const uiState = useAppSelector(state => state.uiState);
    const datasources = useAppSelector(selectDatasources);

    // Redux: Get dispatch function
    const dispatch = useDispatch();

    const handleThemeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Add new message to redux store
        dispatch(
            setTheme(
                stringToEnumValue<typeof ColorScheme, ColorScheme>(
                    ColorScheme,
                    event.target.value
                ) ?? ColorScheme.OS
            )
        );
    };

    function handleInterpretationsLimitChange(value: number | number[]) {
        if (typeof value === "number") {
            dispatch(setInterpretationsLimit(value));
        } else {
            dispatch(setInterpretationsLimit(value[0]));
        }
    }

    function handleTableRowLimitChange(value: number | number[]) {
        if (typeof value === "number") {
            dispatch(setMaxResultsPerTable(value));
        } else {
            dispatch(setMaxResultsPerTable(value[0]));
        }
    }

    function handleRelevancyWeightChange(value: number | number[]) {
        if (typeof value === "number") {
            dispatch(setRelevanceWeight(value));
        } else {
            dispatch(setRelevanceWeight(value[0]));
        }
    }

    function handleComparisonWeightChange(value: number | number[]) {
        if (typeof value === "number") {
            dispatch(setComparisonWeight(value));
        } else {
            dispatch(setComparisonWeight(value[0]));
        }
    }

    function handleQueryWeightChange(value: number | number[]) {
        if (typeof value === "number") {
            dispatch(setQueryWeight(value));
        } else {
            dispatch(setQueryWeight(value[0]));
        }
    }

    function handleFavoriteWeightChange(value: number | number[]) {
        if (typeof value === "number") {
            dispatch(setFavoriteWeight(value));
        } else {
            dispatch(setFavoriteWeight(value[0]));
        }
    }

    return (
        <PageContainer>
            <H1>Settings</H1>
            <P>Your settings are saved automatically.</P>
            <Stack spacing={2}>
                <Paper sx={{ padding: "1em" }}>
                    <H2>Querying preferences</H2>
                    <FormControl sx={{ width: "100%" }} component={"fieldset"}>
                        <FormLabel>
                            Upper limit of interpretations per operator:
                        </FormLabel>
                        <Slider
                            aria-label="Max interpretations"
                            value={currentInterpretationsLimit}
                            onChange={(evt, value) =>
                                handleInterpretationsLimitChange(value)
                            }
                            getAriaValueText={interpretationsValue}
                            valueLabelDisplay="auto"
                            step={1}
                            marks
                            min={1}
                            max={100}
                        />
                        <FormLabel>Max results per table</FormLabel>
                        <Slider
                            aria-label="Max results"
                            defaultValue={100}
                            valueLabelDisplay="auto"
                            step={100}
                            min={0}
                            max={10000}
                            onChange={(evt, value) =>
                                handleTableRowLimitChange(value)
                            }
                        />
                        <FormLabel id="choose-query-language-group-label">
                            Choose the query language
                        </FormLabel>
                        <RadioGroup
                            aria-labelledby="choose-query-language-group-label"
                            value={uiState.selectedQueryLanguage}
                            onChange={(_, value) => {
                                switch (value) {
                                    case "sql":
                                        setQueryLanguage(QueryLanguage.SQL);
                                        break;
                                    default:
                                        console.warn(
                                            "Selected unsupported option. Ignoring it.",
                                            value
                                        );
                                        break;
                                }
                            }}
                            name="query-language-radio-group"
                        >
                            <FormControlLabel
                                value="sql"
                                control={<Radio />}
                                label="SQL"
                            />
                            <FormControlLabel
                                value="sparql"
                                control={<Radio />}
                                label="SparQL"
                            />
                        </RadioGroup>
                        <FormLabel id="choose-datasource-group-label">
                            Choose data source
                        </FormLabel>
                        <DatasourceSelector />
                    </FormControl>
                </Paper>
                <Paper sx={{ padding: "1em" }}>
                    <H2>Degree of Interest</H2>
                    <div style={{ width: "100%" }}>
                        <DoiConfiguration applyAutomatically />
                    </div>
                </Paper>
                <Paper sx={{ padding: "1em" }}>
                    <H2>Look and feel</H2>
                    <FormControl sx={{ maxWidth: "100%" }} component="fieldset">
                        <FormLabel component="legend">Theme</FormLabel>
                        <RadioGroup
                            aria-label="colorscheme"
                            name="colorScheme1"
                            value={currentTheme}
                            onChange={handleThemeChange}
                        >
                            <FormControlLabel
                                value={ColorScheme.OS}
                                control={<Radio />}
                                label="Based on OS theme"
                            />
                            <FormControlLabel
                                value={ColorScheme.LIGHT}
                                control={<Radio />}
                                label="Light theme"
                            />
                            <FormControlLabel
                                value={ColorScheme.DARK}
                                control={<Radio />}
                                label="Dark theme"
                            />
                        </RadioGroup>
                    </FormControl>
                </Paper>
                <Paper sx={{ padding: "1em" }}>
                    <SessionsComponent />
                </Paper>
                <Paper sx={{ padding: "1em" }}>
                    <H2>Utilities</H2>
                    <H3>Predefined sets of interesting columns</H3>
                    {columFavPresets.map(preset => (
                        <Card key={preset.name}>
                            <CardHeader>
                                <H3>preset.name</H3>
                            </CardHeader>
                            <CardContent>
                                {preset.columns.map(col => (
                                    <Chip
                                        label={col}
                                        key={col}
                                        size={"small"}
                                    />
                                ))}
                            </CardContent>
                            <CardActions>
                                <Button
                                    variant={"contained"}
                                    onClick={() => {
                                        preset.columns.forEach(col => {
                                            dispatch(setColumnFav(col, true));
                                        });
                                    }}
                                >
                                    Add to col favs
                                </Button>
                                <Button
                                    onClick={() => {
                                        preset.columns.forEach(col => {
                                            dispatch(setColumnFav(col, false));
                                        });
                                    }}
                                >
                                    Remove from col favs
                                </Button>
                            </CardActions>
                        </Card>
                    ))}
                </Paper>
                <Paper sx={{ padding: "1em" }} key={"fullJson"}>
                    <H2>Currently persisted state:</H2>
                    <Json src={uiState} collapsed={2} />
                </Paper>
            </Stack>
        </PageContainer>
    );
};
